import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, map, tap } from 'rxjs';

import { BaseService } from './base-service';
import { ProjectStatusEntity, ProjectStatusesService } from './project-status.service';
import { ToastService } from './toast.service';
import { ListProjects$Params } from '@api/backend-connector/fn/projects/list-projects';
import {
  BaseProjectDto,
  BaseProjectDtoPage,
  CloneProjectRequest,
  CreateProjectRequest,
  GuidCounterDto,
  ProjectDto,
  UpdateProjectRequest,
} from '@api/backend-connector/models';
import { ProjectsApi } from '@api/backend-connector/services';

export type ProjectData = {
  hideImportWorkpackages?: boolean;
  hideTemplates?: boolean;
};

const toasts = {
  createSuccessSummary: 'i18n.ProjectToast.createSuccessSummary',
  createSuccessDetail: 'i18n.ProjectToast.createSuccessDetail',
  createFailSummary: 'i18n.ProjectToast.createFailSummary',
  createFailDetail: 'i18n.ProjectToast.createFailDetail',

  updateSuccessSummary: 'i18n.ProjectToast.updateSuccessSummary',
  updateSuccessDetail: 'i18n.ProjectToast.updateSuccessDetail',
  updateFailSummary: 'i18n.ProjectToast.updateFailSummary',
  updateFailDetail: 'i18n.ProjectToast.updateFailDetail',

  deleteSuccessSummary: 'i18n.ProjectToast.deleteSuccessSummary',
  deleteSuccessDetail: 'i18n.ProjectToast.deleteSuccessDetail',
  deleteFailSummary: 'i18n.ProjectToast.deleteFailSummary',
  deleteFailDetail: 'i18n.ProjectToast.deleteFailDetail',
};

const showCopySuccessToast = (toast: ToastService) => {
  toast.add({
    severity: 'info',
    summary: 'i18n.ProjectToast.idCopiedSuccessSummary',
    detail: 'i18n.ProjectToast.idCopiedSuccessDetail',
  });
};

const showArchiveSuccessToast = (toast: ToastService) => {
  toast.add({
    severity: 'info',
    summary: 'i18n.ProjectToast.projectArchivedSuccessSummary',
    detail: 'i18n.ProjectToast.projectArchivedSuccessDetail',
  });
};

const getProjectLink = (projectId: string) => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/project/${projectId}`;
};

export const getProjectInterval = (project: ProjectDto) => {
  const [startDate] = [project.startDate, project.minWorkPackageStart]
    .filter((date) => !!date)
    .sort((a, b) => new Date(a as string).getTime() - new Date(b as string).getTime());

  const [endDate] = [project.endDate, project.maxWorkPackageEnd]
    .filter((date) => !!date)
    .sort((a, b) => new Date(b as string).getTime() - new Date(a as string).getTime());

  const interval = {
    start: startDate ? new Date(startDate) : new Date(new Date().getFullYear(), 0, 1),
    end: endDate ? new Date(endDate) : new Date(new Date().getFullYear(), 11, 31),
  };
  return interval;
};

export const buildUpdateProjectInitialRequest = (dto: ProjectDto): UpdateProjectRequest => {
  const laborCost = dto.laborCosts[0];
  if (dto.laborCosts.length > 1) {
    console.warn('Multiple labor cost are not supported by the client', dto.laborCosts);
  }
  return {
    name: dto.name,
    description: dto.description,
    startDate: dto.startDate,
    endDate: dto.endDate,
    status: dto.status,
    projectStatusDescription: dto.projectStatusDescription,
    workPackageTypeIds: dto.workPackageTypes.map((t) => t.id),
    basicCostTypeIds: dto.basicCostTypes.map((t) => t.id),
    laborCostIds: laborCost ? [laborCost.id] : [],
    isArchived: dto.isArchived,
    isPublic: dto.isPublic,
    budgetEnabled: dto.budgetEnabled,
    allowMultipleBudget: dto.allowMultipleBudget,
    costFlowEnabled: dto.costFlowEnabled,
    revenueEnabled: dto.revenueEnabled,
    cashFlowEnabled: dto.cashFlowEnabled,
    workPackageEstimates: dto.workPackageEstimates,
    personEstimates: dto.personEstimates,
    allowBudgetCapOvercome: dto.allowBudgetCapOvercome,
    allowEstimatesOvercome: dto.allowEstimatesOvercome,
    allowNotAllocatedEffectives: dto.allowNotAllocatedEffectives,
    allowUpdateBudgetVersionEstimates: dto.allowUpdateBudgetVersionEstimates,
    allowUpdateActiveBudget: dto.allowUpdateActiveBudget,
    data: dto.data,
  };
};

@Injectable({
  providedIn: 'root',
})
export class ProjectsService extends BaseService {
  override toasts = toasts;

  private readonly projectsApi = inject(ProjectsApi);
  private readonly projectStatusesService = inject(ProjectStatusesService);

  readonly showCopySuccessToast = showCopySuccessToast;
  readonly showArchiveSuccessToast = showArchiveSuccessToast;
  readonly getProjectLink = getProjectLink;

  readonly refresh$ = new BehaviorSubject<void>(undefined);

  listProjects(params: ListProjects$Params): Observable<BaseProjectDtoPage> {
    return this.projectsApi.listProjects(params);
  }

  listRecentProjects(): Observable<BaseProjectDto[]> {
    return this.listProjects({ limit: 4, offset: 0 }).pipe(map(({ items }) => items));
  }

  allProjects(): Observable<BaseProjectDto[]> {
    // FIXME pagination
    return this.projectsApi.listProjects({ limit: 1000, offset: 0 }).pipe(
      tap((page) => {
        if (page.count !== page.items.length) {
          throw new Error('Projects limit execeed (1000)');
        }
      }),
      map(({ items }) => items),
    );
  }

  getProject(params: { id: string }): Observable<ProjectDto> {
    return this.projectsApi.getProject({
      ...params,
    });
  }

  createProject(request: CreateProjectRequest) {
    return this.projectsApi.createProject({ body: request });
  }

  cloneProject(request: CloneProjectRequest) {
    return this.projectsApi.cloneProject({ body: request });
  }

  updateProject(id: string, request: UpdateProjectRequest) {
    return this.projectsApi.updateProject({ id, body: request });
  }

  updateProjectIsArchived(current: ProjectDto, isArchived: boolean) {
    return this.updateProject(current.id, {
      ...buildUpdateProjectInitialRequest(current),
      isArchived,
    });
  }

  deleteProject(params: { id: string }) {
    return this.projectsApi.deleteProject(params);
  }

  getProjectStats(projectId: string): Observable<GuidCounterDto> {
    return this.projectsApi.getProjectStats({ id: projectId });
  }

  getProjectsStats(
    params: Parameters<typeof ProjectsService.prototype.projectsApi.getProjectsStats>[0],
  ): Observable<(ProjectStatusEntity & { value: number })[]> {
    return combineLatest([
      this.projectStatusesService.listProjectStatuses(),
      this.projectsApi.getProjectsStats(params),
    ]).pipe(
      map(([statuses, stats]) => {
        return stats.value.map((val) => {
          const status = statuses.find((s) => s.id === val.key) as ProjectStatusEntity;
          return { ...status, value: val.value };
        });
      }),
    );
  }

  getProjectWorkPackageCategoryGroups(projectId: string) {
    return this.projectsApi.getProjectWorkPackageCategoryGroups({ id: projectId });
  }
}
